import { observable, action, runInAction, computed } from "mobx";
import { getCurrentCulture } from "@utils";

import { LazyImportWithLoadFailHandle as lazy } from "@lib/lazy-import-with-guard/LazyImportWithGuard";
import { BettingActivity } from "@data-types/betting-activity/BettingActivity";
import { LoaderStore } from "@state/stores/common";
import { UserAuthStore } from "./UserAuthStore";
import { DefaultPath } from "@utils/common/defaultRoutePathsEnum";
import { history } from "@state/utils";
import { localizationService } from "@state/services";

const loadFailPath = `/${getCurrentCulture()}/app-update`;

// prettier-ignore
const BettingActivityService = lazy(loadFailPath, ()=>import("@services/betting-activity/BettingActivityService"));

class BettingActivityStore {
	@observable bettingActivityError = null;
	@observable bettingActivityFetchedData: BettingActivity | null = null;

	userAuthStore: UserAuthStore;
	loaderStore: LoaderStore;

	constructor(userAuthStore: UserAuthStore) {
		this.userAuthStore = userAuthStore;
		this.loaderStore = new LoaderStore();
	}

	@computed get isLoading() {
		return this.loaderStore.isLoading;
	}

	@action.bound
	setBettingActivityError(error: any) {
		this.bettingActivityError = error;
	}

	@action.bound
	async fetchBettingActivityData() {
		try {
			this.loaderStore.suspend();

			const response = await (
				await BettingActivityService
			).default.getBettingActivityData();

			runInAction(() => {
				this.bettingActivityFetchedData = response?.data;
			});
		} catch (error) {
			console.error(error);
			this.bettingActivityError = error;
		} finally {
			this.loaderStore.resume();
		}
	}

	@action.bound
	async updateBettingActivityReview() {
		try {
			this.loaderStore.suspend();

			await (
				await BettingActivityService
			).default.updateBettingActivityReview();

			this.userAuthStore.resolveBettingActivity();
		} catch (error) {
			console.error(error);
		} finally {
			this.loaderStore.resume();
		}
	}

	@action.bound
	async logoutUser() {
		try {
			this.loaderStore.suspend();

			await this.userAuthStore.logoutUser();
			history.push(`/${getCurrentCulture()}/${DefaultPath.HOME}`);
		} catch (error) {
			console.error(error);
			this.userAuthStore.rootStore.notificationStore.error(
				localizationService.t(
					"error-handling:GENERIC_ERROR"
				)
			);
		} finally {
			this.loaderStore.resume();
		}
	}

	@action.bound
	handleErrorResponse() {
		this.logoutUser();
		history.push(`/${getCurrentCulture()}/${DefaultPath.HOME}`);
		this.setBettingActivityError(null);
	}
}

export default BettingActivityStore;
